import { ButtonPrimary } from '@/components/button'
import { PasswordInput } from '@/components/input'
import { resetPasswordWithToken } from '@/services'
import { LoginResponse } from '@/types'
import { createSignUpStep2ValidationSchema } from '@/utils/schema/SignUpStep2ValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type FormValues = {
  password: string
  confirmPassword: string
}

const serverError = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback

/**
 * The in-place forced password reset: shown right after the second factor when the server halted
 * the login with a reset requirement that carries a `resetToken`. The user sets a new password
 * here and the same call finishes the login — no emailed code, no second sign-in. If the token has
 * expired (15 minutes) the only way forward is to sign in again, hence "Start over".
 */
export const ForcedPasswordReset = ({
  resetToken,
  email,
  onComplete,
  onStartOver,
}: {
  resetToken: string
  email?: string
  onComplete: (response: LoginResponse) => void
  onStartOver: () => void
}) => {
  const { t } = useTranslation()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | undefined>()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onTouched',
    resolver: yupResolver(createSignUpStep2ValidationSchema(t)),
  })

  const onSubmit = handleSubmit(async (data) => {
    setSubmitting(true)
    setError(undefined)
    try {
      const res = await resetPasswordWithToken({ token: resetToken, password: data.password })
      onComplete(res)
    } catch (e: any) {
      setError(serverError(e, t('auth.forcedReset.failed', 'Could not set that password. Try again.')))
    } finally {
      setSubmitting(false)
    }
  })

  return (
    <form
      className="w-[300px] xs:w-[345px] flex flex-col gap-y-6"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      <div className="flex flex-col gap-y-2">
        <h1 className="text-2xl xs:text-3xl font-bold text-white">
          {t('auth.forcedReset.title', 'Set a new password')}
        </h1>
        <p className="text-sm text-white/70">
          {t(
            'auth.forcedReset.subtitle',
            'Your account requires a new password before you can continue. Choose one you do not use anywhere else.'
          )}
          {email ? ` (${email})` : ''}
        </p>
      </div>

      <PasswordInput
        label={t('common.newPass')}
        placeholder={t('common.newPass')}
        instructs={['Must be at least 6 characters long', 'Must contain one or more numbers']}
        {...register('password')}
        error={errors.password?.message}
        autoFocus
      />
      <PasswordInput
        label={t('common.confirmPass')}
        placeholder={t('common.confirmPass')}
        {...register('confirmPassword')}
        error={errors.confirmPassword?.message}
        onPaste={(e) => e.preventDefault()}
      />

      {error && <p className="text-xs text-warn">{error}</p>}

      <div className="flex flex-col items-center gap-y-4">
        <ButtonPrimary
          type="submit"
          title={submitting ? t('common.saving', 'Saving…') : t('auth.forcedReset.submit', 'Set password and continue')}
          className="w-60 !py-[14px]"
          disabled={submitting}
        />
        <button type="button" onClick={onStartOver} className="text-sm text-white/60 hover:text-white underline underline-offset-4">
          {t('auth.forcedReset.startOver', 'Start over')}
        </button>
      </div>
    </form>
  )
}
